import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { MAX_SAVED_VIEWS } from 'src/app/shared/constants/kpi-catalog';
import {
  SavedView,
  SavedViewFilters,
} from 'src/app/shared/models/dashboard-preferences.model';
import { ReportFilterState } from '../report-filter-bar/report-filter-bar.component';
import { SaveViewNameDialogComponent } from '../save-view-name-dialog/save-view-name-dialog.component';

@Component({
  selector: 'app-kpi-saved-views-bar',
  templateUrl: './kpi-saved-views-bar.component.html',
  styleUrls: ['./kpi-saved-views-bar.component.scss'],
})
export class KpiSavedViewsBarComponent {
  @Input() scope: 'store' | 'chain' = 'store';
  @Input() savedViews: SavedView[] = [];
  @Input() currentFilter: ReportFilterState = { period: 'this_month' };
  @Input() disabled = false;
  @Input() i18nPrefix = 'KPI.OVERVIEW';

  @Output() viewApplied = new EventEmitter<ReportFilterState>();
  @Output() savedViewsChange = new EventEmitter<SavedView[]>();

  readonly maxSavedViews = MAX_SAVED_VIEWS;
  selectedViewId = '';
  saving = false;

  constructor(
    private dialogService: NbDialogService,
    private ecommerceService: EcommerceService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  get scopedViews(): SavedView[] {
    return this.savedViews.filter((view) => view.scope === this.scope);
  }

  get atMaxViews(): boolean {
    return this.scopedViews.length >= MAX_SAVED_VIEWS;
  }

  get saveDisabled(): boolean {
    return this.disabled || this.saving || this.atMaxViews;
  }

  get saveButtonTitle(): string {
    if (this.atMaxViews) {
      return this.translate.instant(`${this.i18nPrefix}.SAVED_VIEWS_MAX_REACHED`, {
        max: MAX_SAVED_VIEWS,
      });
    }
    return '';
  }

  onSelectView(viewId: string): void {
    this.selectedViewId = viewId;
    if (!viewId) {
      return;
    }
    const view = this.scopedViews.find((item) => item.id === viewId);
    if (!view) {
      return;
    }
    this.viewApplied.emit(this.filtersToState(view.filters));
  }

  openSaveDialog(): void {
    if (this.saveDisabled) {
      if (this.atMaxViews) {
        this.toastr.warning(
          this.translate.instant(`${this.i18nPrefix}.SAVED_VIEWS_MAX_REACHED`, {
            max: MAX_SAVED_VIEWS,
          })
        );
      }
      return;
    }

    const dialogRef = this.dialogService.open(SaveViewNameDialogComponent, {
      closeOnBackdropClick: false,
    });

    dialogRef.onClose.subscribe((name: string | undefined) => {
      if (!name) {
        return;
      }
      this.saveCurrentView(name);
    });
  }

  confirmDeleteSelected(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const view = this.scopedViews.find((item) => item.id === this.selectedViewId);
    if (!view) {
      return;
    }
    this.confirmDelete(view, event);
  }

  confirmDelete(view: SavedView, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    Swal.fire({
      title: this.translate.instant('KPI.SAVED_VIEWS.DELETE_CONFIRM_TITLE'),
      text: this.translate.instant('KPI.SAVED_VIEWS.DELETE_CONFIRM_TEXT', { name: view.name }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('KPI.SAVED_VIEWS.DELETE'),
      cancelButtonText: this.translate.instant('KPI.SAVED_VIEWS.CANCEL'),
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteView(view);
      }
    });
  }

  private saveCurrentView(name: string): void {
    const newView: SavedView = {
      id: this.generateViewId(),
      scope: this.scope,
      name,
      filters: this.stateToFilters(this.currentFilter),
      createdAt: new Date().toISOString(),
    };

    const updatedViews = [...this.savedViews, newView];
    this.persistSavedViews(updatedViews, 'KPI.SAVED_VIEWS.SAVED', newView.id);
  }

  private deleteView(view: SavedView): void {
    const updatedViews = this.savedViews.filter((item) => item.id !== view.id);
    if (this.selectedViewId === view.id) {
      this.selectedViewId = '';
    }
    this.persistSavedViews(updatedViews, 'KPI.SAVED_VIEWS.DELETED');
  }

  private persistSavedViews(
    updatedViews: SavedView[],
    successKey: string,
    selectId?: string
  ): void {
    this.saving = true;
    this.ecommerceService.updateDashboardPreferences({ savedViews: updatedViews }).subscribe({
      next: (res: any) => {
        const data = res.response?.data ?? res.data ?? res;
        const views = Array.isArray(data?.savedViews) ? data.savedViews : updatedViews;
        this.savedViewsChange.emit(views);
        if (selectId) {
          this.selectedViewId = selectId;
        }
        this.saving = false;
        this.toastr.success(this.translate.instant(successKey));
      },
      error: (err: any) => {
        this.saving = false;
        this.toastr.error(
          err.error?.response?.message ||
            this.translate.instant('KPI.SAVED_VIEWS.SAVE_ERROR')
        );
      },
    });
  }

  private stateToFilters(state: ReportFilterState): SavedViewFilters {
    const filters: SavedViewFilters = { period: state.period };
    if (state.period === 'custom' && state.startDate && state.endDate) {
      filters.startDate = this.toDateInputValue(state.startDate);
      filters.endDate = this.toDateInputValue(state.endDate);
    }
    return filters;
  }

  private filtersToState(filters: SavedViewFilters): ReportFilterState {
    const state: ReportFilterState = { period: filters.period };
    if (filters.period === 'custom') {
      state.startDate = this.toDateInputValue(filters.startDate);
      state.endDate = this.toDateInputValue(filters.endDate);
    }
    return state;
  }

  private toDateInputValue(value?: string): string | undefined {
    if (!value) {
      return undefined;
    }
    return value.length >= 10 ? value.slice(0, 10) : value;
  }

  private generateViewId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `view-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
}
